// Shared source-bucket definitions, transaction categorization, and the
// module-level cache used by the Finance Dashboard, the Transaction Logs
// table, and the Backfill button on the Security Dashboard.
//
// Splitting this out keeps TransactionLogs.js focused on rendering and
// lets the cache + categorization helpers be unit-tested in isolation.

import { api } from 'common';

// ─────────────────────────────────────────────────────────────────────
// Source buckets — every wallet transaction maps into exactly one
// bucket. Booking sources contribute to GMV/commission; the others are
// operational cash flows.
// ─────────────────────────────────────────────────────────────────────
export const SOURCE_BUCKETS = {
    regular_booking:   { label: 'Regular Booking',    color: '#2196F3' },
    donkey_meter:      { label: 'Donkey Meter',       color: '#FF9800' },
    offline_meter:     { label: 'Offline Meter',      color: '#795548' },
    shared_trip:       { label: 'Shared Trip',        color: '#009688' },
    top_up:            { label: 'Top-Up Credit',      color: '#4CAF50' },
    admin_credit:      { label: 'Admin Credit',       color: '#3F51B5' },
    cancellation_fee:  { label: 'Cancellation Fee',   color: '#F44336' },
    tip:               { label: 'Tip',                color: '#FFC107' },
    weekly_goal_bonus: { label: 'Weekly Goal Bonus',  color: '#9C27B0' },
    flat_rate_fee:     { label: 'Daily Flat-Rate Fee', color: '#1B5E20' },
    mile_redeem:       { label: 'Mile Redemption',    color: '#E91E63' },
    // Mile-only redemptions (no wallet movement) — fed exclusively by the
    // explicit `source` stamped in /transactionStats by the redemption
    // Cloud Functions; no legacy haystack patterns exist for them.
    mile_airtime:      { label: 'Mile Airtime',       color: '#00BCD4' },
    mile_donation:     { label: 'Mile Donation',      color: '#FF4081' },
    meter_reward:      { label: 'Meter Reward Card',  color: '#7CB342' },
    wallet_transfer:   { label: 'Wallet Transfer',    color: '#607D8B' },
    withdraw:          { label: 'Withdrawal',         color: '#FF5722' },
    withdraw_refund:   { label: 'Withdraw Refund',    color: '#8BC34A' },
    // One-time flat-rate welcome credit granted by complete_onboarding
    // (server stamps `source`; no legacy haystack patterns exist for it).
    welcome_credit:    { label: 'Driver Welcome Credit', color: '#26A69A' },
    // Holiday registration gift (New Year): the same one-time prepaid-day
    // credit, granted at sign-up during the campaign window (userCreate).
    holiday_promo:     { label: 'New Year Gift',       color: '#F9A825' },
    other:             { label: 'Other',              color: '#9E9E9E' },
};

// The four revenue-producing booking types. GMV, Driver Earnings, and
// Platform Commission on the Dashboard sum amounts under these sources
// only — everything else is operational cash flow, not marketplace
// value.
export const BOOKING_SOURCES = ['regular_booking', 'donkey_meter', 'shared_trip', 'offline_meter'];

// Categorize a wallet transaction into a source bucket.
// Reads the explicit `source` field if present (newer writes set it
// server-side); falls back to pattern-matching txRef + remarks for
// legacy records that have no source field.
export const getTransactionSource = (transaction) => {
    // 1. Server-set explicit source field (preferred — new writes have this).
    if (transaction.source && SOURCE_BUCKETS[transaction.source]) {
        return { key: transaction.source, ...SOURCE_BUCKETS[transaction.source] };
    }

    // 2. Distinctive non-string fields (more reliable than text matching).
    //    `type === 'Processing'` is unique to in-flight withdrawal requests
    //    (see functions/index.js withdraw flow). `operatorUid` is only
    //    written by the admin top-up action (paymentactions.js addMoney),
    //    so its presence reliably identifies an admin-driven top-up
    //    regardless of the operator's UID string format.
    if (transaction.type === 'Processing') return { key: 'withdraw', ...SOURCE_BUCKETS.withdraw };
    if (transaction.operatorUid)           return { key: 'top_up', ...SOURCE_BUCKETS.top_up };

    // 3. Pattern match legacy records via txRef + remarks. Order matters:
    // more-specific patterns first so e.g. "Weekly goal bonus" doesn't get
    // swallowed by a generic "bonus" check.
    const txRef = String(transaction.txRef || '');
    const remarks = String(transaction.remarks || '');
    const haystack = (txRef + ' ' + remarks).toLowerCase();

    if (haystack.includes('weekly goal'))      return { key: 'weekly_goal_bonus', ...SOURCE_BUCKETS.weekly_goal_bonus };
    if (haystack.includes('redeemmile') || haystack.includes('redeem mile')) return { key: 'mile_redeem', ...SOURCE_BUCKETS.mile_redeem };
    if (haystack.includes('wallet_to_topup') || haystack.includes('topup balance') || haystack.includes('wallet balance transfer')) return { key: 'wallet_transfer', ...SOURCE_BUCKETS.wallet_transfer };
    if (haystack.includes('cancelled_withdraw') || haystack.includes('withdraw cancellation') || haystack.includes('withdrawal cancellation')) return { key: 'withdraw_refund', ...SOURCE_BUCKETS.withdraw_refund };
    if (haystack.includes('withdraw'))         return { key: 'withdraw', ...SOURCE_BUCKETS.withdraw };
    if (haystack.includes('admin credit'))     return { key: 'admin_credit', ...SOURCE_BUCKETS.admin_credit };
    if (haystack.includes('cancellation'))     return { key: 'cancellation_fee', ...SOURCE_BUCKETS.cancellation_fee };
    if (haystack.includes('tip'))              return { key: 'tip', ...SOURCE_BUCKETS.tip };
    if (haystack.includes('top') && haystack.includes('up')) return { key: 'top_up', ...SOURCE_BUCKETS.top_up };
    // Payment-gateway top-ups (Chapa, Telebirr, Santimpay, PayPal, etc.)
    // historically wrote `txRef: "wallet-<uid>-<rand>"` with no other
    // distinguishing field. Pre-deploy records of this kind fall here.
    if (txRef.startsWith('wallet-') || txRef.startsWith('wallet_')) return { key: 'top_up', ...SOURCE_BUCKETS.top_up };
    if (haystack.includes('donkey meter') || haystack.includes('donkey_meter') || haystack.includes('meter ride')) return { key: 'donkey_meter', ...SOURCE_BUCKETS.donkey_meter };
    if (haystack.includes('offline meter') || haystack.includes('offline_meter')) return { key: 'offline_meter', ...SOURCE_BUCKETS.offline_meter };
    if (haystack.includes('shared trip') || haystack.includes('shared_trip')) return { key: 'shared_trip', ...SOURCE_BUCKETS.shared_trip };

    // 4. Firebase push-key pattern (starts with `-` followed by ~19 chars).
    //    Legacy commission entries are stamped with the bare booking ID — without
    //    joining the bookings record we can't distinguish regular vs meter vs
    //    shared. Bucket as Regular Booking for now; server-side `source` field
    //    on new writes will disambiguate going forward.
    if (/^-[a-zA-Z0-9_-]{18,}$/.test(txRef)) return { key: 'regular_booking', ...SOURCE_BUCKETS.regular_booking };

    return { key: 'other', ...SOURCE_BUCKETS.other };
};

// ─────────────────────────────────────────────────────────────────────
// Stats cache — shared across the Dashboard and the Logs tab's
// rangeStats fetch. 5-minute TTL keeps the page snappy on tab switches
// and quick re-visits while staying recent enough for admin work; data
// older than 5 min auto-refreshes on next read. The "Refresh" link on
// the Dashboard and the Backfill button bypass the cache for an
// explicit hard refresh.
//
// Module-level so it survives component remounts (no need to hoist
// into context). Memory cost is negligible — even "All Time" of stats
// is well under 1 MB.
// ─────────────────────────────────────────────────────────────────────
export const STATS_TTL_MS = 5 * 60 * 1000;
const _statsCache = new Map(); // key: `${startKey}|${endKey}` → { data, ts }

export const fetchStatsCached = (dispatch, startKey, endKey, opts) => {
    const force = !!(opts && opts.force);
    const key = `${startKey || 'ALL'}|${endKey || 'ALL'}`;
    const cached = _statsCache.get(key);
    if (!force && cached && (Date.now() - cached.ts) < STATS_TTL_MS) {
        // Resolve via Promise.resolve so callers can always .then() it.
        return Promise.resolve(cached.data);
    }
    return Promise.resolve(dispatch(api.fetchTransactionStats(startKey || null, endKey || null)))
        .then((data) => {
            _statsCache.set(key, { data, ts: Date.now() });
            return data;
        });
};

// Public knob — called by the Backfill button (and anywhere else
// that mutates /transactionStats) so the next read goes to the server.
export const invalidateStatsCache = () => { _statsCache.clear(); };

// Stash on window for the Backfill button's success callback to find
// without importing across module boundaries. Cheap, no module surgery.
if (typeof window !== 'undefined') {
    window.__invalidateTransactionStatsCache = invalidateStatsCache;
}
